
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-gray-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <img 
              src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
              alt="GAMBGO Logo"
              className="w-6 h-6 sm:w-8 sm:h-8"
            />
            <span className="text-base sm:text-lg font-bold text-gray-900">GAMBGO</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Last updated: June 8, 2025
          </p>
        </div>

        <div className="prose prose-sm sm:prose-lg max-w-none">
          <div className="bg-blue-50 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <p className="text-blue-900 font-medium text-sm sm:text-base">
              At GAMBGO, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our food and grocery delivery platform in The Gambia.
            </p>
          </div>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 text-sm sm:text-base">
              <li>Name, phone number, and email address</li>
              <li>Delivery addresses and location data within The Gambia</li>
              <li>Payment information and transaction history</li>
              <li>Order history and preferences</li>
              <li>Profile photos and user-generated content</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">Usage Information</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 text-sm sm:text-base">
              <li>App usage patterns and interactions</li>
              <li>Device information and identifiers</li>
              <li>Location data for delivery purposes within The Gambia</li>
              <li>Customer service communications</li>
              <li>Search queries and browsing behavior</li>
            </ul>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base">
              <li>Process and fulfill your orders efficiently</li>
              <li>Provide customer support and assistance</li>
              <li>Send order updates and delivery notifications via SMS and in-app</li>
              <li>Improve our services and user experience</li>
              <li>Prevent fraud and ensure platform security</li>
              <li>Send promotional offers (with your explicit consent)</li>
              <li>Analyze usage patterns to enhance app performance</li>
              <li>Comply with local Gambian regulations and tax requirements</li>
            </ul>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">3. Information Sharing</h2>
            <p className="text-gray-700 mb-4 text-sm sm:text-base">
              We may share your information with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base">
              <li><strong>Restaurant and vendor partners:</strong> Only necessary information to fulfill your orders</li>
              <li><strong>Delivery drivers:</strong> Contact and delivery address information to complete deliveries</li>
              <li><strong>Payment processors:</strong> Secure transaction processing through certified providers</li>
              <li><strong>Service providers:</strong> Third-party companies that help us operate our platform</li>
              <li><strong>Legal authorities:</strong> When required by Gambian law or court orders</li>
              <li><strong>Business transfers:</strong> In case of merger, acquisition, or sale of business assets</li>
            </ul>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">4. Data Security & Protection</h2>
            <p className="text-gray-700 mb-4 text-sm sm:text-base">
              We implement comprehensive security measures to protect your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base">
              <li>End-to-end encryption of sensitive data in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Restricted access to personal information on a need-to-know basis</li>
              <li>Secure payment processing through PCI DSS certified providers</li>
              <li>Multi-factor authentication for admin accounts</li>
              <li>Regular backup and disaster recovery procedures</li>
              <li>Compliance with international data protection standards</li>
            </ul>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">5. Your Rights & Choices</h2>
            <p className="text-gray-700 mb-4 text-sm sm:text-base">
              Under Gambian law and international standards, you have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base">
              <li>Access and review your personal information</li>
              <li>Update, correct, or modify your data</li>
              <li>Delete your account and associated data permanently</li>
              <li>Opt-out of marketing communications at any time</li>
              <li>Request data portability in machine-readable format</li>
              <li>Object to certain data processing activities</li>
              <li>File complaints with relevant data protection authorities</li>
            </ul>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">6. Cookies & Tracking Technologies</h2>
            <p className="text-gray-700 mb-4 text-sm sm:text-base">
              We use cookies, web beacons, and similar technologies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm sm:text-base">
              <li>Remember your preferences and login status</li>
              <li>Analyze app usage and performance metrics</li>
              <li>Provide personalized content and restaurant recommendations</li>
              <li>Ensure platform security and prevent fraudulent activities</li>
              <li>Enable social sharing features</li>
              <li>Support targeted advertising (with your consent)</li>
            </ul>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">7. Third-Party Services & Integrations</h2>
            <p className="text-gray-700 text-sm sm:text-base">
              Our app integrates with various third-party services including payment processors, mapping services, analytics providers, and social media platforms. These services have their own privacy policies, and we encourage you to review them. We carefully vet all third-party partners to ensure they maintain appropriate data protection standards.
            </p>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">8. Data Retention & Storage</h2>
            <p className="text-gray-700 text-sm sm:text-base">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Transaction records are kept for 7 years as required by Gambian tax law. Account information is retained until you request deletion. You can request immediate deletion of your data through your account settings or by contacting our support team.
            </p>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 text-sm sm:text-base">
              While we primarily operate within The Gambia, some of our service providers may be located in other countries. We ensure that any international data transfers are protected by appropriate safeguards, including standard contractual clauses and adequacy decisions where applicable.
            </p>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 text-sm sm:text-base">
              GAMBGO is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will delete such information.
            </p>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 text-sm sm:text-base">
              We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes through the app, email, or prominent notices on our website. Your continued use of GAMBGO after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
              <p className="text-gray-700 mb-4 text-sm sm:text-base">
                If you have any questions about this Privacy Policy, wish to exercise your rights, or have concerns about how we handle your data, please contact us:
              </p>
              <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                <li><strong>Email:</strong> privacy@gambgo.com</li>
                <li><strong>Phone:</strong> +2202554992</li>
                <li><strong>Address:</strong> GAMBGO Headquarters, Sukuta, The Gambia</li>
                <li><strong>Data Protection Officer:</strong> dpo@gambgo.com</li>
                <li><strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM GMT</li>
              </ul>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 sm:py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
              alt="GAMBGO Logo"
              className="w-6 h-6 sm:w-8 sm:h-8"
            />
            <span className="text-lg sm:text-xl font-bold">GAMBGO</span>
          </div>
          <p className="text-gray-400 text-sm sm:text-base">&copy; 2026 GAMBGO. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
